import { useEffect, useState, useCallback } from "react";
import { api, dateShort } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Pencil, Users as UsersIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const emptyForm = { email: "", name: "", role: "operator", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setEditId(u.id); setOpen(true);
  };

  const submit = async () => {
    try {
      if (editId) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        if (!form.email || !form.password || form.password.length < 6)
          return toast.error("E-posta ve en az 6 karakter şifre zorunlu");
        await api.post("/users", form);
        toast.success("Kullanıcı oluşturuldu");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`"${u.name}" kullanıcısını silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`${u.email} için yeni şifre (en az 6 karakter):`);
    if (!pw) return;
    if (pw.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    try {
      await api.put(`/users/${u.id}`, { password: pw });
      toast.success("Şifre güncellendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="p-8">
      <PageHeader
        eyebrow="Erişim Yönetimi"
        title="Kullanıcılar"
        subtitle="Yönetici ve operatör hesaplarını buradan oluşturun"
        actions={<Button data-testid="user-new-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Yeni Kullanıcı</Button>}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>E-posta</Label>
              <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} placeholder="ornek@firma.com" />
            </div>
            <div>
              <Label>Ad Soyad</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ali Yılmaz" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operatör (sadece üretim panosu)</SelectItem>
                  <SelectItem value="admin">Yönetici (tam yetki)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editId ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre"}</Label>
              <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="En az 6 karakter" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="user-save-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {list.length === 0 ? (
        <EmptyState icon={UsersIcon} title="Henüz kullanıcı yok" description="İlk yönetici veya operatör hesabını ekleyin." />
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-widest">Ad</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">E-posta</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Rol</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Oluşturma</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((u) => (
                <TableRow key={u.id} className="hover:bg-slate-50">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-blue-600 text-white grid place-items-center font-bold text-xs">
                        {(u.name || u.email).slice(0, 1).toUpperCase()}
                      </div>
                      <span>{u.name}</span>
                      {u.id === me?.id && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">Ben</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600 font-mono text-xs">{u.email}</TableCell>
                  <TableCell>
                    {u.role === "admin"
                      ? <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Yönetici</Badge>
                      : <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Operatör</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{dateShort(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" title="Şifre sıfırla" onClick={() => resetPw(u)}><KeyRound className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(u)} disabled={u.id === me?.id}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
